import * as React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  LineChart,
  Pie,
  PieChart,
  ReferenceLine,
  TooltipProps,
  XAxis,
  YAxis
} from "recharts";
import {
  ChartColumn,
  ChartColumnDecreasing,
  ChartColumnStacked,
  ChartLine,
  ChartPie
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  type ChartConfig
} from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { usePreferences } from "@/lib/preferences";
import { cn } from "@/lib/utils";
import {
  MonthYear,
  monthKey,
  monthLabel,
  monthsInRange
} from "@/components/MonthRangePicker";
import {
  DateRangePicker,
  DateRangeValue,
  isWithinRange
} from "@/components/DateRangePicker";
import {
  Advance,
  AdvanceApproval,
  Approval,
  FinanceQueueRow,
  TravelRequestSummary
} from "@/types";

// Travel Requests and Advances each get their own card (see
// EntityVolumeCard below), each internally stacked by status by default.
// Status keeps the same 3 colors across both entities, so each card's
// legend shows the same 3 swatches.
// Labels only, no `color` - every mode fills straight from STATUS_COLOR
// below (index.css's --chart-* tokens, themselves derived from the app's
// theme green/amber/red), the single source for bars, slices, legend and
// tooltip alike. ChartStyle's generated <style data-chart=...> block was
// found by hand-testing not to resolve var(--chart-*) into its
// --color-<key> variables, which is why the stacked bars used to carry
// their own literal hex here and drifted from every other mode.
const chartConfig = {
  requestApproved: { label: "Approved" },
  requestPending: { label: "Pending" },
  requestRejected: { label: "Rejected" },
  advanceApproved: { label: "Approved" },
  advancePending: { label: "Pending" },
  advanceRejected: { label: "Rejected" }
} satisfies ChartConfig;

type Status = "Approved" | "Pending" | "Rejected";
type Bucket = {
  month: string;
  requestApproved: number;
  requestPending: number;
  requestRejected: number;
  advanceApproved: number;
  advancePending: number;
  advanceRejected: number;
};

// Maps each entity's own status vocabulary onto the three shared chart
// buckets. Travel requests and advances have different status enums (see
// db/requests_store.py / db/advances_store.py) but both collapse onto the
// same "where does this stand" meaning for the chart.
function requestStatus(status: TravelRequestSummary["status"]): Status | null {
  switch (status) {
    case "Draft":
      return null; // never actually submitted - excluded
    // ResubmitRequested (sent back to the employee for changes) isn't
    // resolved either way yet - same "still waiting" meaning as
    // Submitted/PendingFinance (mirrors advanceStatus()'s Resubmit -> Pending).
    case "Submitted":
    case "PendingFinance":
    case "ResubmitRequested":
      return "Pending";
    case "Approved":
    case "Completed":
      return "Approved";
    case "Rejected":
      return "Rejected";
    default:
      return null;
  }
}

function advanceStatus(status: Advance["status"]): Status {
  switch (status) {
    // Resubmit (sent back to the employee for changes) isn't resolved
    // either way yet - same "still waiting" meaning as Pending (mirrors
    // requestStatus()'s ResubmitRequested -> Pending).
    case "Pending":
    case "Resubmit":
      return "Pending";
    case "Approved":
    case "Paid":
      return "Approved";
    case "Rejected":
      return "Rejected";
  }
}

function approvalStatus(
  status:
    | Approval["status"]
    | FinanceQueueRow["status"]
    | AdvanceApproval["status"]
): Status {
  return status; // ApprovalStatus is already exactly 'Pending' | 'Approved' | 'Rejected'
}

function monthKeyOf(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function toMonthYear(d: Date): MonthYear {
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

// One bucket per calendar month the day-level range touches (a partial
// first/last month still gets its own bar); items outside the exact days
// are excluded by isWithinRange in the bucketers below.
function emptyBuckets(range: DateRangeValue): Map<string, Bucket> {
  const start = toMonthYear(range.from);
  const end = toMonthYear(range.to);
  const map = new Map<string, Bucket>();
  for (const m of monthsInRange(start, end)) {
    map.set(monthKey(m), {
      month: monthLabel(m),
      requestApproved: 0,
      requestPending: 0,
      requestRejected: 0,
      advanceApproved: 0,
      advancePending: 0,
      advanceRejected: 0
    });
  }
  return map;
}

/** Buckets travel requests + advances by their own createdAt into two
 * side-by-side, status-stacked bars per month (Travel Request, Advance),
 * across the range's days, inclusive. */
export function bucketSubmissions(
  requests: TravelRequestSummary[],
  advances: Advance[],
  range: DateRangeValue
): Bucket[] {
  const buckets = emptyBuckets(range);
  for (const r of requests) {
    const status = requestStatus(r.status);
    if (!status || !isWithinRange(r.createdAt, range)) continue;
    const bucket = buckets.get(monthKeyOf(r.createdAt));
    if (bucket) bucket[`request${status}`] += 1;
  }
  for (const a of advances) {
    if (!isWithinRange(a.createdAt, range)) continue;
    const status = advanceStatus(a.status);
    const bucket = buckets.get(monthKeyOf(a.createdAt));
    if (bucket) bucket[`advance${status}`] += 1;
  }
  return [...buckets.values()];
}

/** Where an advance stands from the FINANCE APPROVER's side, once a Manager
 * has approved it: waiting on them (awaiting payment) is Pending, paid out
 * is Approved, and turned down by Finance is Rejected. A Rejected advance
 * with no financeDecision was rejected by its Manager and never reached
 * Finance, so it isn't an item of theirs (null). */
function financeStageStatus(advance: Advance): Status | null {
  switch (advance.status) {
    case "Approved":
      return "Pending";
    case "Paid":
      return "Approved";
    case "Rejected":
      return advance.financeDecision ? "Rejected" : null;
    default:
      return null;
  }
}

/** Buckets approval-stage rows by the approval's own createdAt (when routed
 * to the approver) rather than the underlying request's submission date -
 * travel-request approvals (Manager/Finance stage) as one bar, advance
 * approvals as the other.
 *
 * An advance's own approval record (advanceRows) is the MANAGER's decision -
 * a Finance Approver's later decision (pay it, or reject it) isn't on that
 * record, so on its own the chart never saw a Finance rejection: only a
 * Manager's. `financeAdvances` (a Finance user's advances at the Finance
 * stage - see fetchFinanceAdvanceStage) adds that stage, as travel requests
 * already get theirs from their Finance-queue rows. Each advance is counted
 * once: one already represented by an approval row of the user's own (they
 * were its direct approver) isn't added again - unless Finance has since
 * rejected it, in which case that final outcome replaces the earlier row. */
export function bucketApprovals(
  requestRows: (Approval | FinanceQueueRow)[],
  advanceRows: AdvanceApproval[],
  range: DateRangeValue,
  financeAdvances: Advance[] = []
): Bucket[] {
  const buckets = emptyBuckets(range);
  for (const row of requestRows) {
    if (!isWithinRange(row.createdAt, range)) continue;
    const bucket = buckets.get(monthKeyOf(row.createdAt));
    if (bucket) bucket[`request${approvalStatus(row.status)}`] += 1;
  }
  const rejectedByFinance = new Set(
    financeAdvances
      .filter((a) => financeStageStatus(a) === "Rejected")
      .map((a) => a.id)
  );
  const alreadyCounted = new Set<string>();
  for (const row of advanceRows) {
    if (rejectedByFinance.has(row.advanceId)) continue; // superseded by the Finance rejection below
    alreadyCounted.add(row.advanceId);
    if (!isWithinRange(row.createdAt, range)) continue;
    const bucket = buckets.get(monthKeyOf(row.createdAt));
    if (bucket) bucket[`advance${approvalStatus(row.status)}`] += 1;
  }
  for (const advance of financeAdvances) {
    const status = financeStageStatus(advance);
    if (!status || alreadyCounted.has(advance.id)) continue;
    // Routed to Finance when the Manager approved it - the same "when it
    // reached this approver" date the rows above are bucketed by.
    const routedAt = advance.approval?.decidedAt ?? advance.createdAt;
    if (!isWithinRange(routedAt, range)) continue;
    const bucket = buckets.get(monthKeyOf(routedAt));
    if (bucket) bucket[`advance${status}`] += 1;
  }
  return [...buckets.values()];
}

// recharts gives every series in a stack the same fixed corner radius
// regardless of stacking order, so when the "top" series (Rejected) is 0 for
// a given month, whichever segment actually renders on top shows a flat
// edge instead of a rounded one. This draws each stacked segment as a
// manually-built rounded rect, rounding only the corners that are genuinely
// at the outer top/bottom of that bar for that specific data point - i.e.
// only the topmost and bottommost segments that actually have a non-zero
// value get rounded, no matter which status that turns out to be.
function makeStackedBarShape(group: "request" | "advance", statusKey: Status) {
  const keys = (["Approved", "Pending", "Rejected"] as Status[]).map(
    (s) => `${group}${s}` as keyof Bucket
  );
  const thisKey = `${group}${statusKey}` as keyof Bucket;

  return (props: any) => {
    const { x, y, width, height, payload, fill } = props;
    const nonZeroKeys = keys.filter((k) => Number(payload[k]) > 0);
    const isBottom = nonZeroKeys[0] === thisKey;
    const isTop = nonZeroKeys[nonZeroKeys.length - 1] === thisKey;
    const r = 4;

    // A single non-zero segment in the stack: round all 4 corners.
    // Otherwise round only the end(s) that are genuinely outermost.
    const topRadius = isTop ? r : 0;
    const bottomRadius = isBottom ? r : 0;

    if (topRadius === 0 && bottomRadius === 0) {
      return <rect x={x} y={y} width={width} height={height} fill={fill} />;
    }

    const path = `
      M ${x},${y + topRadius}
      Q ${x},${y} ${x + topRadius},${y}
      L ${x + width - topRadius},${y}
      Q ${x + width},${y} ${x + width},${y + topRadius}
      L ${x + width},${y + height - bottomRadius}
      Q ${x + width},${y + height} ${x + width - bottomRadius},${y + height}
      L ${x + bottomRadius},${y + height}
      Q ${x},${y + height} ${x},${y + height - bottomRadius}
      Z
    `;
    return <path d={path} fill={fill} />;
  };
}

// ── Separate Travel Request / Advance charts ────────────────────────────
// The two entities used to share one chart (two adjacent bars per month).
// They're now two cards side by side (stacked on narrower screens), each
// charting only its own half of the same Bucket[] - no extra fetches, and
// the bucketing above is untouched. The view selector (Org-wide / approval
// queue / my submissions) and month range stay ONE shared toolbar above
// both cards, since they drive the same data; each card has its own
// chart-type switcher in its top-right corner.
type Entity = "request" | "advance";

const ENTITY_META: Record<
  Entity,
  { title: string; noun: [string, string]; color: string }
> = {
  request: {
    title: "Travel Requests",
    noun: ["travel request", "travel requests"],
    color: "var(--chart-request)"
  },
  advance: {
    title: "Advances",
    noun: ["advance", "advances"],
    color: "var(--chart-advance)"
  }
};

type ChartType = "stacked" | "grouped" | "trend" | "donut" | "pareto";

// "Donut" is the default (listed first); "Stacked" is the same
// status-stacked bar each entity had in the original combined chart.
const CHART_TYPES: {
  value: ChartType;
  label: string;
  hint: string;
  icon: LucideIcon;
}[] = [
  {
    value: "donut",
    label: "Donut",
    hint: "Status share across the range",
    icon: ChartPie
  },
  {
    value: "stacked",
    label: "Stacked bars",
    hint: "Monthly volume, split by status",
    icon: ChartColumnStacked
  },
  {
    value: "grouped",
    label: "Grouped bars",
    hint: "Statuses side by side each month",
    icon: ChartColumn
  },
  {
    value: "trend",
    label: "Trend line",
    hint: "Monthly total over time",
    icon: ChartLine
  },
  {
    value: "pareto",
    label: "Pareto",
    hint: "Busiest months, ranked",
    icon: ChartColumnDecreasing
  }
];

// A per-viewer convenience (like the theme toggle), kept per card so each
// chart remembers its own type across a refresh. Storage can be unavailable
// (private window, blocked site data) - every access is guarded and falls
// back to the default.
const chartTypeStorageKey = (entity: Entity) =>
  `asmara-dashboard-chart-type:${entity}`;

function readStoredChartType(entity: Entity): ChartType {
  try {
    const stored = localStorage.getItem(chartTypeStorageKey(entity));
    if (CHART_TYPES.some((t) => t.value === stored)) return stored as ChartType;
  } catch {
    // ignore - fall through to the default
  }
  return "donut";
}

function storeChartType(entity: Entity, type: ChartType) {
  try {
    localStorage.setItem(chartTypeStorageKey(entity), type);
  } catch {
    // ignore - the choice just won't persist
  }
}

const STATUSES: Status[] = ["Approved", "Pending", "Rejected"];

const STATUS_COLOR: Record<Status, string> = {
  Approved: "var(--chart-approved)",
  Pending: "var(--chart-pending)",
  Rejected: "var(--chart-rejected)"
};

/** One entity's slice of a month bucket. */
type EntityRow = {
  month: string;
  Approved: number;
  Pending: number;
  Rejected: number;
  total: number;
};

function entityRows(data: Bucket[], entity: Entity): EntityRow[] {
  return data.map((b) => {
    const Approved = b[`${entity}Approved` as keyof Bucket] as number;
    const Pending = b[`${entity}Pending` as keyof Bucket] as number;
    const Rejected = b[`${entity}Rejected` as keyof Bucket] as number;
    return {
      month: b.month,
      Approved,
      Pending,
      Rejected,
      total: Approved + Pending + Rejected
    };
  });
}

// One decimal place for a single, standalone share (e.g. the tooltip, which
// shows only the hovered slice) - not Math.round, so the figure is always
// the true nearest-tenth value rather than a coarser whole-number rounding.
function pct(part: number, whole: number): string {
  return whole > 0 ? `${((part / whole) * 100).toFixed(1)}%` : "0%";
}

// For a SET of shares shown together (the donut's legend list) - rounding
// each one to one decimal independently can still visibly miss 100% (e.g.
// 2/7, 2/7, 3/7 → 28.6%/28.6%/42.9% independently round to 100.1%).
// Largest-remainder method:
// round every value down to one decimal first, then hand out the leftover
// tenths (100 minus what the floors sum to) to whichever values were
// closest to rounding up - so the displayed figures always add to exactly
// 100.0% while staying as close as possible to the true share.
function pctList(parts: number[]): string[] {
  const wholeTotal = parts.reduce((sum, p) => sum + p, 0);
  if (wholeTotal <= 0) return parts.map(() => "0%");
  const scaled = parts.map((p) => (p / wholeTotal) * 1000); // tenths of a percent
  const floors = scaled.map((s) => Math.floor(s));
  let remainder = 1000 - floors.reduce((sum, f) => sum + f, 0);
  const order = scaled
    .map((s, i) => ({ i, frac: s - floors[i] }))
    .sort((a, b) => b.frac - a.frac);
  const tenths = [...floors];
  for (const { i } of order) {
    if (remainder <= 0) break;
    tenths[i] += 1;
    remainder -= 1;
  }
  return tenths.map((t) => `${(t / 10).toFixed(1)}%`);
}

function countLabel(n: number, entity: Entity): string {
  const [one, many] = ENTITY_META[entity].noun;
  return `${n} ${n === 1 ? one : many}`;
}

function Swatch({
  color,
  line,
  dashed
}: {
  color: string;
  line?: boolean;
  dashed?: boolean;
}) {
  if (line) {
    return (
      <span
        className="h-0 w-3.5 shrink-0 border-t-2"
        style={{ borderColor: color, borderStyle: dashed ? "dashed" : "solid" }}
      />
    );
  }
  return (
    <span
      className="h-2 w-2 shrink-0 rounded-[2px]"
      style={{ backgroundColor: color }}
    />
  );
}

function LegendRow({
  items,
  note
}: {
  items: { label: string; color: string; line?: boolean; dashed?: boolean }[];
  note?: string;
}) {
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 pt-3 text-xs text-muted-foreground">
      <div className="flex flex-wrap items-center gap-4">
        {items.map((item) => (
          <span key={item.label} className="flex items-center gap-1.5">
            <Swatch color={item.color} line={item.line} dashed={item.dashed} />
            {item.label}
          </span>
        ))}
      </div>
      {note && (
        <>
          <span className="text-muted-foreground/70">·</span>
          <span>{note}</span>
        </>
      )}
    </div>
  );
}

const STATUS_LEGEND = STATUSES.map((s) => ({
  label: s,
  color: STATUS_COLOR[s]
}));

const TOOLTIP_BOX =
  "grid min-w-[11rem] gap-1.5 rounded-lg border border-border/50 bg-background px-3 py-2 text-xs shadow-xl";

function TooltipLine({
  color,
  label,
  value,
  line
}: {
  color?: string;
  label: string;
  value: React.ReactNode;
  line?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="flex items-center gap-1.5 text-muted-foreground">
        {color && <Swatch color={color} line={line} />}
        {label}
      </span>
      <span className="font-mono font-medium tabular-nums text-foreground">
        {value}
      </span>
    </div>
  );
}

/** Month + Approved/Pending/Rejected + total - for the Stacked, Grouped and
 *  Trend views. Reads the hovered month's EntityRow, which every one of
 *  those views carries on its data points. */
function StatusTooltip({
  active,
  payload,
  label
}: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload as EntityRow;
  return (
    <div className={TOOLTIP_BOX}>
      <div className="font-medium">{label}</div>
      {STATUSES.map((s) => (
        <TooltipLine key={s} color={STATUS_COLOR[s]} label={s} value={row[s]} />
      ))}
      <div className="border-t pt-1.5">
        <TooltipLine label="Total" value={row.total} />
      </div>
    </div>
  );
}

/** Stacked - the original chart's bar for this entity, unchanged:
 *  same colours (chartConfig), same rounded-segment shape. */
function StackedView({ data, entity }: { data: Bucket[]; entity: Entity }) {
  const rows = React.useMemo(() => {
    const slice = entityRows(data, entity);
    return data.map((b, i) => ({ ...b, ...slice[i] }));
  }, [data, entity]);
  return (
    <>
      <ChartContainer config={chartConfig} className="max-h-[16.25rem] w-full">
        <BarChart accessibilityLayer data={rows}>
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="month"
            tickLine={false}
            tickMargin={10}
            axisLine={false}
          />
          <ChartTooltip content={<StatusTooltip />} />
          {STATUSES.map((s) => (
            <Bar
              key={s}
              dataKey={`${entity}${s}`}
              stackId={entity}
              fill={STATUS_COLOR[s]}
              shape={makeStackedBarShape(entity, s)}
              maxBarSize={48}
            />
          ))}
        </BarChart>
      </ChartContainer>
      <LegendRow items={STATUS_LEGEND} />
    </>
  );
}

/** Grouped - Approved / Pending / Rejected as three bars side by side each
 *  month, for comparing statuses directly rather than as parts of a total. */
function GroupedView({ data, entity }: { data: Bucket[]; entity: Entity }) {
  const rows = React.useMemo(() => entityRows(data, entity), [data, entity]);
  const { uiScale } = usePreferences();
  return (
    <>
      <ChartContainer config={chartConfig} className="max-h-[16.25rem] w-full">
        <BarChart accessibilityLayer data={rows} barGap={2}>
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="month"
            tickLine={false}
            tickMargin={10}
            axisLine={false}
          />
          <YAxis
            allowDecimals={false}
            tickLine={false}
            axisLine={false}
            width={Math.round(28 * uiScale)}
          />
          <ChartTooltip content={<StatusTooltip />} />
          {STATUSES.map((s) => (
            <Bar
              key={s}
              dataKey={s}
              fill={STATUS_COLOR[s]}
              radius={[4, 4, 0, 0]}
              maxBarSize={20}
            />
          ))}
        </BarChart>
      </ChartContainer>
      <LegendRow items={STATUS_LEGEND} />
    </>
  );
}

/** Trend - the entity's monthly total (all statuses) as one line. */
function TrendView({ data, entity }: { data: Bucket[]; entity: Entity }) {
  const rows = React.useMemo(() => entityRows(data, entity), [data, entity]);
  const color = ENTITY_META[entity].color;
  const { uiScale } = usePreferences();
  return (
    <>
      <ChartContainer config={chartConfig} className="max-h-[16.25rem] w-full">
        <LineChart
          accessibilityLayer
          data={rows}
          margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
        >
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="month"
            tickLine={false}
            tickMargin={10}
            axisLine={false}
          />
          <YAxis
            allowDecimals={false}
            tickLine={false}
            axisLine={false}
            width={Math.round(28 * uiScale)}
          />
          <ChartTooltip content={<StatusTooltip />} />
          <Line
            type="monotone"
            dataKey="total"
            stroke={color}
            strokeWidth={2}
            dot={{ r: 4, fill: color, strokeWidth: 0 }}
            activeDot={{ r: 5, stroke: "hsl(var(--card))", strokeWidth: 2 }}
          />
        </LineChart>
      </ChartContainer>
      <LegendRow
        items={[
          { label: `${ENTITY_META[entity].title} per month`, color, line: true }
        ]}
        note="All statuses"
      />
    </>
  );
}

type DonutSlice = { status: Status; value: number };

function DonutTooltip({
  active,
  payload,
  total
}: TooltipProps<number, string> & { total: number }) {
  if (!active || !payload?.length) return null;
  const slice = payload[0].payload as DonutSlice;
  return (
    <div className={TOOLTIP_BOX}>
      <TooltipLine
        color={STATUS_COLOR[slice.status]}
        label={slice.status}
        value={`${slice.value} · ${pct(slice.value, total)}`}
      />
    </div>
  );
}

/** Donut - this entity's status share across the whole selected range, with
 *  the total in the centre and a count/percentage list beside it (so the
 *  numbers never depend on reading angles alone). */
function DonutView({ data, entity }: { data: Bucket[]; entity: Entity }) {
  const slices: DonutSlice[] = React.useMemo(() => {
    const rows = entityRows(data, entity);
    return STATUSES.map((s) => ({
      status: s,
      value: rows.reduce((sum, r) => sum + r[s], 0)
    }));
  }, [data, entity]);
  const total = slices.reduce((sum, s) => sum + s.value, 0);
  const visible = slices.filter((s) => s.value > 0);
  const percentages = React.useMemo(
    () => pctList(slices.map((s) => s.value)),
    [slices]
  );

  return (
    // flex-wrap, not sm:flex-row: the legend drops under the donut whenever
    // the card itself is too narrow for both, whatever the screen width.
    <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-8">
      <div className="relative h-[12.5rem] w-[12.5rem] shrink-0">
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-semibold tabular-nums">{total}</span>
          <span className="text-xs text-muted-foreground">in range</span>
        </div>
        <ChartContainer
          config={chartConfig}
          className="relative z-10 aspect-square h-full w-full"
        >
          <PieChart>
            <ChartTooltip content={<DonutTooltip total={total} />} />
            <Pie
              data={visible}
              dataKey="value"
              nameKey="status"
              innerRadius={56}
              outerRadius={88}
              stroke="hsl(var(--card))"
              strokeWidth={2}
              isAnimationActive={false}
            >
              {visible.map((s) => (
                <Cell key={s.status} fill={STATUS_COLOR[s.status]} />
              ))}
            </Pie>
          </PieChart>
        </ChartContainer>
      </div>
      <div className="min-w-[12rem] max-w-xs flex-1 space-y-2 text-sm">
        {slices.map((s, i) => (
          <div
            key={s.status}
            className="flex items-center justify-between gap-4 border-b pb-2 last:border-b-0"
          >
            <span className="flex items-center gap-2">
              <Swatch color={STATUS_COLOR[s.status]} />
              <span className="font-medium">{s.status}</span>
            </span>
            <span className="tabular-nums">
              <span className="font-semibold">{s.value}</span>
              <span className="ml-2 text-xs text-muted-foreground">
                {percentages[i]}
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

type ParetoRow = {
  month: string;
  total: number;
  share: number;
  cumulative: number;
};

function ParetoTooltip({ active, payload }: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload as ParetoRow;
  return (
    <div className={TOOLTIP_BOX}>
      <div className="font-medium">{row.month}</div>
      <TooltipLine label="Raised" value={row.total} />
      <TooltipLine
        color="var(--chart-volume)"
        label="Share of volume"
        value={`${Math.round(row.share)}%`}
      />
      <TooltipLine
        color="hsl(var(--foreground))"
        line
        label="Cumulative share"
        value={`${Math.round(row.cumulative)}%`}
      />
    </div>
  );
}

/** Pareto - this entity's months ranked by volume (busiest first): bars =
 *  each month's share of the range's total, line = running cumulative
 *  share. Both are percentages on ONE 0–100% axis - no second y-scale.
 *  Months with nothing raised are left out (they add nothing to the
 *  ranking). */
function ParetoView({ data, entity }: { data: Bucket[]; entity: Entity }) {
  const { uiScale } = usePreferences();
  const rows: ParetoRow[] = React.useMemo(() => {
    const totals = entityRows(data, entity)
      .map((r) => ({ month: r.month, total: r.total }))
      .filter((r) => r.total > 0)
      .sort((a, b) => b.total - a.total);
    const grand = totals.reduce((sum, r) => sum + r.total, 0);
    let running = 0;
    return totals.map((r) => {
      running += r.total;
      return {
        ...r,
        share: (r.total / grand) * 100,
        cumulative: (running / grand) * 100
      };
    });
  }, [data, entity]);

  return (
    <>
      <ChartContainer config={chartConfig} className="max-h-[16.25rem] w-full">
        <ComposedChart
          accessibilityLayer
          data={rows}
          margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
        >
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="month"
            tickLine={false}
            tickMargin={10}
            axisLine={false}
          />
          <YAxis
            domain={[0, 100]}
            ticks={[0, 25, 50, 75, 100]}
            tickFormatter={(v) => `${v}%`}
            tickLine={false}
            axisLine={false}
            width={Math.round(40 * uiScale)}
          />
          <ChartTooltip content={<ParetoTooltip />} />
          <ReferenceLine
            y={80}
            stroke="hsl(var(--muted-foreground))"
            strokeDasharray="4 4"
          />
          <Bar
            dataKey="share"
            fill="var(--chart-volume)"
            radius={[4, 4, 0, 0]}
            maxBarSize={48}
          />
          <Line
            type="monotone"
            dataKey="cumulative"
            stroke="hsl(var(--foreground))"
            strokeWidth={2}
            dot={{ r: 4, fill: "hsl(var(--foreground))", strokeWidth: 0 }}
            activeDot={{ r: 5, stroke: "hsl(var(--card))", strokeWidth: 2 }}
          />
        </ComposedChart>
      </ChartContainer>
      <LegendRow
        items={[
          { label: "Share of volume", color: "var(--chart-volume)" },
          {
            label: "Cumulative share",
            color: "hsl(var(--foreground))",
            line: true
          },
          {
            label: "80% mark",
            color: "hsl(var(--muted-foreground))",
            line: true,
            dashed: true
          }
        ]}
        note="Busiest first"
      />
    </>
  );
}

/** Chart-type switcher: a compact row of icon buttons in the card's
 *  top-right corner, the active one filled in the primary colour. Behaves
 *  as a radio group (one always selected) - each button names itself via
 *  aria-label and a hover/focus tooltip, since the icons carry no text. */
function ChartTypeToggle({
  value,
  onChange,
  entity
}: {
  value: ChartType;
  onChange: (value: ChartType) => void;
  entity: Entity;
}) {
  return (
    <TooltipProvider delayDuration={200}>
      <div
        role="radiogroup"
        aria-label={`${ENTITY_META[entity].title} chart type`}
        className="flex shrink-0 items-center gap-0.5 rounded-md border p-0.5"
      >
        {CHART_TYPES.map(({ value: v, label, hint, icon: Icon }) => {
          const active = v === value;
          return (
            <Tooltip key={v}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  role="radio"
                  aria-checked={active}
                  aria-label={label}
                  onClick={() => onChange(v)}
                  
                className={cn(
                "flex h-7 w-7 shrink-0 items-center justify-center rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                active
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}  

                >
                  <Icon className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                <span className="block font-medium">{label}</span>
                <span className="block opacity-80">{hint}</span>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}

/** One entity's card: title + in-range total, chart-type switcher top-right,
 *  and its own empty state. */
/** Placeholder shown while the chart's data is still loading - bar-shaped
 *  skeletons at the chart's own height, so the card doesn't jump when the
 *  real chart replaces it. */
function ChartSkeleton() {
  const heights = [45, 70, 55, 85, 60, 75];
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex h-[13.75rem] items-end justify-around gap-3 px-2 pb-6"
    >
      {heights.map((h, i) => (
        <Skeleton
          key={i}
          className="w-full max-w-[2.5rem] rounded-b-none"
          style={{ height: `${h}%` }}
        />
      ))}
      <span className="sr-only">Loading chart…</span>
    </div>
  );
}

function EntityVolumeCard({
  entity,
  data,
  isLoading
}: {
  entity: Entity;
  data: Bucket[];
  isLoading?: boolean;
}) {
  const [chartType, setChartType] = React.useState<ChartType>(() =>
    readStoredChartType(entity)
  );
  const changeChartType = (type: ChartType) => {
    setChartType(type);
    storeChartType(entity, type);
  };

  const total = React.useMemo(
    () => entityRows(data, entity).reduce((sum, r) => sum + r.total, 0),
    [data, entity]
  );
  const { title, noun } = ENTITY_META[entity];

  return (
    <Card className="min-w-0">
      <CardHeader className="space-y-2 pb-2">
        <div className="min-w-0">
          <CardTitle className="text-base">{title}</CardTitle>
          <CardDescription className="whitespace-nowrap">
            {isLoading ? (
              <Skeleton className="mt-1 h-4 w-40" />
            ) : (
              `${countLabel(total, entity)} in this range`
            )}
          </CardDescription>
        </div>
        <div className="self-end">
          <ChartTypeToggle
            value={chartType}
            onChange={changeChartType}
            entity={entity}
          />
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <ChartSkeleton />
        ) : total === 0 ? (
          <div className="flex h-[13.75rem] items-center justify-center text-sm text-muted-foreground">
            No {noun[1]} for this range.
          </div>
        ) : chartType === "grouped" ? (
          <GroupedView data={data} entity={entity} />
        ) : chartType === "trend" ? (
          <TrendView data={data} entity={entity} />
        ) : chartType === "stacked" ? (
          <StackedView data={data} entity={entity} />
        ) : chartType === "pareto" ? (
          <ParetoView data={data} entity={entity} />
        ) : (
          <DonutView data={data} entity={entity} />
        )}
      </CardContent>
    </Card>
  );
}

export function DashboardVolumeChart({
  title,
  description,
  data,
  range,
  onRangeChange,
  viewSelector,
  isLoading = false
}: {
  title: string;
  description: string;
  data: Bucket[];
  range: DateRangeValue;
  onRangeChange: (range: DateRangeValue) => void;
  /** Rendered before the date-range picker in the shared toolbar - e.g. a
   * Select letting the viewer switch between Org-wide/Approval-queue/
   * My-submissions. Optional so this component stays usable standalone (a
   * single view, no picker) if ever needed elsewhere. */
  viewSelector?: React.ReactNode;
  /** While true, both cards show a loading placeholder instead of their
   *  chart or empty state. */
  isLoading?: boolean;
}) {
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 flex-[1_1_16rem]">
          {/* When a viewSelector is provided, its own selected option already
              names the view (e.g. "My submissions") - showing that same
              string again as a heading right next to it would say it twice,
              so only the description doubles as the header text. */}
          {!viewSelector && (
            <h2 className="text-base font-semibold">{title}</h2>
          )}
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {viewSelector}
          {/* No "All dates" here - the charts always need a range to bucket. */}
          <DateRangePicker
            value={range}
            onChange={(r) => r && onRangeChange(r)}
            align="end"
          />
        </div>
      </div>
      {/* Columns by the room in rem, not a px breakpoint (lg:grid-cols-2),
         so at a larger text size (lib/preferences.tsx) the two cards stack
         instead of squeezing - a viewport breakpoint never sees that size. */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,28rem),1fr))] gap-4">
        <EntityVolumeCard entity="request" data={data} isLoading={isLoading} />
        <EntityVolumeCard entity="advance" data={data} isLoading={isLoading} />
      </div>
    </section>
  );
}
